import { usePublicBootstrap } from "../seo/PublicBootstrap.jsx";
import { useEffect, useMemo, useState } from "react";
import {
  normalizeHexColor,
  createBrandTheme,
} from "@mts/utils";
import { fetchCurrentTenant, getTenantSlugFromHostname } from "../services/api";
import {
  getHomepageHeroSettingValue,
  getSettingValue,
} from "../utils/homepageHeroSettings";
import { getTenantFaviconUrl } from "../utils/faviconSettings";
import { getTenantLogoUrl } from "../utils/logoSettings";
import { resolvePublicContact } from "../utils/publicContact";
import { setPostHogTenantContext } from "../lib/posthog";

let cachedTenant = null;
let cachedTenantError = null;
let tenantPromise = null;
const DEFAULT_BODY_FONT = "Inter, system-ui, sans-serif";
const DEFAULT_HEADING_FONT = "Montserrat, Inter, system-ui, sans-serif";

function slugToTitleCase(slug) {
  if (!slug) return "My Town Spotlight";
  const words = slug
    .split(/[-_]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1));

  if (!words.length) return "My Town Spotlight";

  const joined = words.join(" ");
  return /spotlight/i.test(joined) ? joined : `${joined} Spotlight`;
}

function getDefaultSpotlightName() {
  const slug = getTenantSlugFromHostname();
  return slugToTitleCase(slug);
}

function normalizeUrl(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function normalizeNumber(value, fallback) {
  const parsed = Number(value);
  if (Number.isFinite(parsed)) {
    return parsed;
  }
  return fallback;
}

// Color helper functions are now imported from @mts/utils:
// - normalizeHexColor, hexToRgb, rgbToHex, rgbToHsl, hslToRgb
// - mixHex, darkenHex, desaturateHex, getReadableTextColor
// - getPrimaryForegroundColor, normalizeBadgeTextColor, getHeroOverlayColor, getSecondaryColor

function resolveSpotlightName(tenant) {
  const fromSettings = getSettingValue(tenant, "tenant.name");
  if (typeof fromSettings === "string" && fromSettings.trim()) {
    return fromSettings.trim();
  }

  const fromTenant = tenant?.name;
  if (typeof fromTenant === "string" && fromTenant.trim()) {
    return fromTenant.trim();
  }

  return getDefaultSpotlightName();
}

function resolveCityName({ tenant, spotlightName }) {
  const fromSettings = getSettingValue(tenant, "tenant.city");
  if (typeof fromSettings === "string" && fromSettings.trim()) {
    return fromSettings.trim();
  }

  const cleanedSpotlightName = String(spotlightName || "")
    .replace(/\s+spotlight$/i, "")
    .trim();

  if (cleanedSpotlightName) {
    return cleanedSpotlightName;
  }

  return "My Town";
}

function loadTenant() {
  if (tenantPromise) {
    return tenantPromise;
  }

  tenantPromise = fetchCurrentTenant()
    .then((tenant) => {
      cachedTenant = tenant || null;
      cachedTenantError = null;
      return cachedTenant;
    })
    .catch((error) => {
      cachedTenant = null;
      cachedTenantError = error;
      throw error;
    })
    .finally(() => {
      tenantPromise = null;
    });

  return tenantPromise;
}

export function usePublicTenant() {
  const bootstrap = usePublicBootstrap();
  const initialTenant = bootstrap?.tenant || (typeof window !== "undefined" ? cachedTenant : null);
  const [tenant, setTenant] = useState(initialTenant);
  const [error, setError] = useState(initialTenant ? null : cachedTenantError);
  const [loading, setLoading] = useState(!initialTenant);

  useEffect(() => {
    if (bootstrap?.tenant) return undefined;
    let cancelled = false;

    loadTenant()
      .then((data) => {
        if (!cancelled) {
          setTenant(data);
          setError(null);
        }
      })
      .catch((loadError) => {
        if (!cancelled) {
          setTenant(null);
          setError(loadError);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [bootstrap?.tenant]);

  useEffect(() => {
    setPostHogTenantContext(tenant);
  }, [tenant]);

  const spotlightName = useMemo(() => {
    return resolveSpotlightName(tenant);
  }, [tenant]);

  const contactEmail = useMemo(() => {
    return resolvePublicContact(tenant).email;
  }, [tenant]);

  const contactPhone = useMemo(() => {
    return resolvePublicContact(tenant).phone;
  }, [tenant]);

  const tenantLogoConfig = useMemo(() => {
    const cityName = resolveCityName({ tenant, spotlightName });

    return {
      cityName,
      brandName: "SPOTLIGHT",
    };
  }, [spotlightName, tenant]);

  const tenantLogoImageUrl = useMemo(() => {
    return getTenantLogoUrl(tenant);
  }, [tenant]);

  const tenantFaviconUrl = useMemo(() => {
    return getTenantFaviconUrl(tenant);
  }, [tenant]);

  const heroImageUrl = useMemo(() => {
    return normalizeUrl(getHomepageHeroSettingValue(tenant, "image_url"));
  }, [tenant]);

  const heroOverlayStrength = useMemo(() => {
    const raw = normalizeNumber(getHomepageHeroSettingValue(tenant, "overlay_strength"), 68);
    return Math.max(0, Math.min(100, raw));
  }, [tenant]);

  const heroFocalY = useMemo(() => {
    const raw = normalizeNumber(getHomepageHeroSettingValue(tenant, "focal_y"), 50);
    return Math.max(12, Math.min(88, raw));
  }, [tenant]);

  const heroFocalX = useMemo(() => {
    const raw = normalizeNumber(getHomepageHeroSettingValue(tenant, "focal_x"), 50);
    return Math.max(12, Math.min(88, raw));
  }, [tenant]);

  const heroStyle = useMemo(() => {
    const raw = String(getHomepageHeroSettingValue(tenant, "style", "classic") || "classic").trim().toLowerCase();
    if (["classic", "editorial", "minimal"].includes(raw)) {
      return raw;
    }
    return "classic";
  }, [tenant]);

  const brandTheme = useMemo(() => {
    const primaryColor = normalizeHexColor(getSettingValue(tenant, "branding.primary_color"), "#1D4ED8");
    const secondaryColor = normalizeHexColor(getSettingValue(tenant, "branding.secondary_color"), null);
    const accentColor = normalizeHexColor(getSettingValue(tenant, "branding.accent_color"), null);

    return createBrandTheme({
      primaryColor,
      secondaryColor,
      accentColor,
    });
  }, [tenant]);
  const bodyFont = getSettingValue(tenant, "branding.font_body") || DEFAULT_BODY_FONT;
  const headingFont = getSettingValue(tenant, "branding.font_heading") || DEFAULT_HEADING_FONT;

  const websiteStatus = useMemo(() => {
    const raw = String(tenant?.websiteSettings?.status || "PUBLISHED").trim().toUpperCase();
    return raw === "MAINTENANCE" ? "maintenance" : "published";
  }, [tenant]);

  const optInImageUrl = useMemo(() => {
    return normalizeUrl(getSettingValue(tenant, "branding.optin_image_url"));
  }, [tenant]);

  const optInFocalX = useMemo(() => {
    const raw = normalizeNumber(getSettingValue(tenant, "branding.optin_focal_x"), 50);
    return Math.max(12, Math.min(88, raw));
  }, [tenant]);

  const optInFocalY = useMemo(() => {
    const raw = normalizeNumber(getSettingValue(tenant, "branding.optin_focal_y"), 50);
    return Math.max(12, Math.min(88, raw));
  }, [tenant]);

  return {
    tenant,
    spotlightName,
    contactEmail,
    contactPhone,
    tenantLogoConfig,
    tenantLogoImageUrl,
    tenantFaviconUrl,
    heroImageUrl,
    heroOverlayStrength,
    heroFocalX,
    heroFocalY,
    heroStyle,
    brandTheme,
    bodyFont,
    headingFont,
    websiteStatus,
    optInImageUrl,
    optInFocalX,
    optInFocalY,
    loading,
    error,
  };
}
